import {
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  DialogActions,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import {
  useSkuListQuery,
  useSkuImportFromCsvMutation,
} from "../../../__generated__/types-and-hooks";
import { useCallback, useState, useRef } from "react";
import { useTeam } from "../TeamLayout";
import { useNotify } from "../../../hooks/useNotify";
import SmartToySharpIcon from "@mui/icons-material/SmartToySharp";

export interface SkuImportModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SkuImportModal({ open, onClose }: SkuImportModalProps) {
  const teamCtx = useTeam();
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [skuImportFromCsvMutation] = useSkuImportFromCsvMutation();
  const t = useTranslation();
  const skuListQuery = useSkuListQuery({
    variables: { teamId: teamCtx.team.id },
  });

  const [saving, setSaving] = useState(false);
  const notify = useNotify();

  const handleSave = useCallback(async () => {
    setSaving(true);

    const importedSkus = await skuImportFromCsvMutation({
      variables: {
        teamId: teamCtx.team.id,
        text: textAreaRef.current?.value ?? "",
      },
    });

    setSaving(false);
    onClose();
    skuListQuery.refetch();
    if (importedSkus.errors) {
      notify({ type: "danger", msg: "An error has been occurred." });
    } else {
      notify({
        type: "success",
        msg: "New SKUs has been created.",
      });
    }
  }, [notify, skuListQuery, skuImportFromCsvMutation, teamCtx, onClose]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("Import SKUs from text")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography level="body-sm" mb={2}>
              Paste raw CSV data. Make sure the first line contains headers
              (e.g., SKU, Name, UPC, etc.).
            </Typography>

            <Textarea
              slotProps={{ textarea: { ref: textAreaRef } }}
              minRows={10}
              placeholder={`Example:\nSKU,Name,UPC\n12345,T-Shirt,9876543210`}
            />
          </Stack>
        </DialogContent>
        <Typography level="body-xs" color="neutral">
          * AI will parse and import SKUs automatically
        </Typography>
        <DialogActions>
          <Button
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            startDecorator={<SmartToySharpIcon />}
          >
            {t("Import")}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
